/**
 * Phase 14.8 — guarded localStorage proxy.
 *
 * In Chrome / Brave / Safari incognito with "Block all cookies" /
 * "Block all site data" enabled, every access to `window.localStorage`
 * throws `SecurityError: Access is denied for this document.` If that
 * throw lands inside a useState initializer at first render, the
 * entire React tree unmounts — the user sees a "flash then blank
 * white" screen.
 *
 * `safeStorage` mirrors the Web Storage API (`getItem`, `setItem`,
 * `removeItem`, `clear`) and fails closed: if the browser refuses,
 * we fall back to an in-memory map for the duration of the tab.
 *
 * Usage: replace `localStorage.getItem('k')` with `safeStorage.getItem('k')`.
 * Behavior is identical when storage is available; never throws.
 */
const _mem = new Map();

function _canUse() {
    try {
        if (typeof window === "undefined" || !window.localStorage) return false;
        const probe = "__piqpath_probe__";
        window.localStorage.setItem(probe, "1");
        window.localStorage.removeItem(probe);
        return true;
    } catch {
        return false;
    }
}

const _ok = _canUse();

const safeStorage = {
    getItem(key) {
        if (_ok) {
            try { return window.localStorage.getItem(key); } catch { /* fall through */ }
        }
        return _mem.has(key) ? _mem.get(key) : null;
    },
    setItem(key, value) {
        if (_ok) {
            try { window.localStorage.setItem(key, value); return; } catch { /* fall through */ }
        }
        _mem.set(key, String(value));
    },
    removeItem(key) {
        if (_ok) {
            try { window.localStorage.removeItem(key); return; } catch { /* fall through */ }
        }
        _mem.delete(key);
    },
    clear() {
        if (_ok) {
            try { window.localStorage.clear(); return; } catch { /* fall through */ }
        }
        _mem.clear();
    },
    /** Test-hook: did the real localStorage probe succeed? */
    isPersistent: _ok,
};

export default safeStorage;
